/** Density, family filter and search. One line, collapses gracefully. */
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';

import { familyBadge, familyName } from './format';
import type { Density } from './model/types';

interface Props {
  density: Density;
  onDensityChange: (d: Density) => void;
  families: string[];
  selectedFamilies: string[];
  onFamiliesChange: (families: string[]) => void;
  search: string;
  onSearchChange: (value: string) => void;
  showEmpty: boolean;
  onShowEmptyChange: (value: boolean) => void;
  counts: { notifications: number; live: number };
}

export function TimelineToolbar({
  density,
  onDensityChange,
  families,
  selectedFamilies,
  onFamiliesChange,
  search,
  onSearchChange,
  showEmpty,
  onShowEmptyChange,
  counts,
}: Props) {
  const toggleFamily = (family: string) => {
    onFamiliesChange(
      selectedFamilies.includes(family)
        ? selectedFamilies.filter((f) => f !== family)
        : [...selectedFamilies, family],
    );
  };

  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{ alignItems: 'center', flexWrap: 'wrap', rowGap: 1, mb: 1.5 }}
    >
      <ToggleButtonGroup
        size="small"
        exclusive
        value={density}
        onChange={(_, value: Density | null) => value && onDensityChange(value)}
        aria-label="Density"
      >
        <ToggleButton value="compact" sx={{ px: 1.5, py: 0.25 }}>
          Compact
        </ToggleButton>
        <ToggleButton value="detailed" sx={{ px: 1.5, py: 0.25 }}>
          Detailed
        </ToggleButton>
      </ToggleButtonGroup>

      {families.length > 1 && (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Families
          </Typography>
          {families.map((family) => {
            const active = selectedFamilies.length === 0 || selectedFamilies.includes(family);
            return (
              <Tooltip key={family} title={familyName(family)}>
                <Chip
                  size="small"
                  label={familyBadge(family)}
                  onClick={() => toggleFamily(family)}
                  variant={active ? 'filled' : 'outlined'}
                  color={active ? 'primary' : 'default'}
                  sx={{
                    height: 20,
                    minWidth: 28,
                    fontSize: '0.6875rem',
                    fontWeight: 700,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                  }}
                />
              </Tooltip>
            );
          })}
          {selectedFamilies.length > 0 && (
            <Tooltip title="Show all families">
              <IconButton size="small" onClick={() => onFamiliesChange([])} aria-label="Show all families">
                <ClearIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}

      <TextField
        size="small"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Filter by name or event"
        sx={{ width: 240 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={() => onSearchChange('')} aria-label="Clear filter">
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        }}
      />

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={showEmpty}
            onChange={(e) => onShowEmptyChange(e.target.checked)}
          />
        }
        label={<Typography variant="body2">Unused events</Typography>}
      />

      <Box sx={{ flex: 1 }} />

      <Typography variant="body2" color="text.secondary">
        <strong>{counts.live}</strong> of {counts.notifications} will send
      </Typography>
    </Stack>
  );
}
